import { useCallback, useMemo } from "react";
import { useFetch } from "../useFetch";
import type {
	AccountResponse,
	CreateAccountPayload,
	UpdateAccountPayload,
	UpdateAccountStatusPayload,
} from "../../core/types/services";

const filterAccountsByProvider = (
	accounts: AccountResponse[],
	providerId?: number,
) => {
	if (!providerId || providerId <= 0) return [];

	return accounts.filter(
		(account) =>
			account.provider_id === providerId &&
			(account.is_active === undefined || account.is_active),
	);
};

// ============================================
// GET ALL ACCOUNTS
// ============================================
export function useAccounts(auto = true) {
	return useFetch<AccountResponse[]>({
		endpoint: "/account",
		method: "GET",
		auto,
	});
}

// ============================================
// GET ACCOUNTS BY PROVIDER
// ============================================
export function useAccountsByProvider(providerId?: number, auto = true) {
	const fetch = useFetch<AccountResponse[]>({
		endpoint: "/account",
		method: "GET",
		auto: auto && typeof providerId === "number" && providerId > 0,
	});
	const { execute } = fetch;

	const accountsByProvider = useMemo(
		() =>
			filterAccountsByProvider(
				Array.isArray(fetch.data) ? fetch.data : [],
				providerId,
			),
		[fetch.data, providerId],
	);

	const fetchByProvider = useCallback(
		async (targetProviderId: number) => {
			const payload = await execute({ endpoint: "/account" });
			const accounts = Array.isArray(payload) ? payload : [];
			return filterAccountsByProvider(accounts, targetProviderId);
		},
		[execute],
	);

	return {
		...fetch,
		accountsByProvider,
		fetchByProvider,
	};
}

// ============================================
// GET ACCOUNT BY ID
// ============================================
export function useAccount(id: number) {
	return useFetch<AccountResponse>({
		endpoint: `/account/${id}`,
		method: "GET",
	});
}

// ============================================
// CREATE ACCOUNT
// ============================================
export function useCreateAccount() {
	const fetch = useFetch<AccountResponse, CreateAccountPayload>({
		endpoint: "/account",
		method: "POST",
		auto: false,
	});

	const create = (data: CreateAccountPayload) => {
		return fetch.execute({ body: data });
	};

	return { ...fetch, create };
}

// ============================================
// UPDATE ACCOUNT (General Data)
// ============================================
export function useUpdateAccount(id?: number) {
	const fetch = useFetch<AccountResponse, UpdateAccountPayload>({
		endpoint: `/account/${id ?? 0}`,
		method: "PATCH",
		auto: false,
	});

	const update = (
		accountIdOrData: number | UpdateAccountPayload,
		data?: UpdateAccountPayload,
	) => {
		if (typeof accountIdOrData === "number") {
			if (!data) {
				return Promise.reject(
					new Error("Debe enviar los datos para actualizar la cuenta"),
				);
			}

			return fetch.execute({
				endpoint: `/account/${accountIdOrData}`,
				body: data,
			});
		}

		if (!id) {
			return Promise.reject(
				new Error("Se requiere el id de la cuenta para actualizar"),
			);
		}

		return fetch.execute({
			endpoint: `/account/${id}`,
			body: accountIdOrData,
		});
	};

	return { ...fetch, update };
}

// ============================================
// UPDATE ACCOUNT STATUS
// ============================================
export function useUpdateAccountStatus(id: number) {
	const fetch = useFetch<AccountResponse, UpdateAccountStatusPayload>({
		endpoint: `/account/${id}/status`,
		method: "PATCH",
		auto: false,
	});

	const setStatus = (isActive: boolean) => {
		return fetch.execute({ body: { isActive } });
	};

	return { ...fetch, setStatus };
}

// ============================================
// DELETE ACCOUNT
// ============================================
export function useDeleteAccount() {
	const fetch = useFetch<unknown>({
		endpoint: "/account",
		method: "DELETE",
		auto: false,
	});

	const remove = (id: number) => {
		return fetch.execute({ endpoint: `/account/${id}` });
	};

	return { ...fetch, remove };
}
